import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';
import localization from '../../i18n/localization';
import ModalImage from '../Modal/ModalImage';

function desc(a, b, orderBy) {
  let thiz = a[orderBy];
  let that = b[orderBy];
  if (moment(thiz, 'DD.MM.YYYY HH:mm').isValid()) {
    thiz = moment(thiz, 'DD.MM.YYYY HH:mm');
    that = moment(that, 'DD.MM.YYYY HH:mm');
  }
  if (that < thiz) {
    return -1;
  }
  if (that > thiz) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc'
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

const statusToColorMap = {
  PENDING: 'blueText',
  RESERVED: 'greenText',
  BOOKED: 'greenText',
  BOOKED_AND_TRANSFERRED: 'greenText',
  BOOKED_AND_REJECTED: 'redText',
  CANCELED: 'redText',
  CANCELED_AND_TRANSFERRED: 'redText',
  CANCELED_AND_REJECTED: 'redText',
  CORRECTED: 'greenText',
  NO_STATUS: 'blueText',
  NOT_PAID: 'blueText',
  PAID: 'greenText',
  REFUNDED: 'redText',
  REFUND_AND_TRANSFERRED: 'redText',
  REFUND_AND_REJECTED: 'redText',
  ISSUED: 'greenText',
  DELIVERED: 'greenText',
  PRODUCED: 'greenText',
  CONSUMED: 'blueText',
  BLOCKED: 'redText',
  RENEWED: 'greenText',
  EXPIRED: 'blueText',
};

const tableHeadStyles = () => ({
  tableCell: {
    paddingLeft: 8,
    paddingRight: 8,
  },
});

class EnhancedTableHead extends React.Component {
  createSortHandler = property => event => {
    this.props.onRequestSort(event, property);
  };

  render() {
    const { order, orderBy, columnDefinition, classes } = this.props;

    return (
      <TableHead>
        <TableRow>
          {columnDefinition.map((header, index) => {
            return (
              <TableCell
                key={index}
                numeric={header.numeric}
                sortDirection={orderBy === header.id ? order : false}
                className={classes.tableCell}
              >
                <Tooltip title="Sort" placement="bottom-start" enterDelay={300}>
                  <TableSortLabel
                    active={orderBy === header.id}
                    direction={order}
                    onClick={this.createSortHandler(header.id)}
                  >
                    {header.label}
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
            );
          }, this)}
        </TableRow>
      </TableHead>
    );
  }
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  orderBy: PropTypes.string.isRequired,
};

const styles = theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing.unit * 3,
  },
  table: {
    minWidth: 400,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  tableCell: {
    paddingLeft: 8,
    paddingRight: 8,
  },
  canceledTableCell: {
    paddingLeft: 8,
    paddingRight: 8,
    textDecoration: 'line-through',
  },
  redTableCell: {
    paddingLeft: 8,
    paddingRight: 8,
    color: '#ff0000',
  },
  label: {
    borderRadius: 4,
    padding: 4,
    backgroundColor: '#e0e0e0',
    marginRight: 8,
  },
  consumedLabel: {
    borderRadius: 4,
    padding: 4,
    backgroundColor: 'rgb(44, 147, 16)',
    marginRight: 8,
    color: '#ffffff',
  },
  labelRow: {
    marginBottom: 4,
  },
  redText: {
    backgroundColor: '#ff0000',
    fontWeight: 'bold',
    borderRadius: 4,
    padding: 4,
    color: '#ffffff',
  },
  greenText: {
    backgroundColor: 'rgb(44, 147, 16)',
    fontWeight: 'bold',
    borderRadius: 4,
    padding: 4,
    color: '#ffffff',
  },
  blueText: {
    backgroundColor: 'rgb(0, 172, 193)',
    fontWeight: 'bold',
    borderRadius: 4,
    padding: 4,
    color: '#ffffff',
  },
});

class EnhancedTable extends React.Component {
  static defaultProps = {
    onRowClick: () => {},
    data: [],
    hover: true,
  };

  state = {
    order: 'asc',
    orderBy: '',
    page: 0,
    rowsPerPage: 5,
  };

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = 'desc';

    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    this.setState({ order, orderBy });
  };

  handleChangePage = (event, page) => {
    this.setState({ page });
  };

  handleChangeRowsPerPage = event => {
    this.setState({ rowsPerPage: event.target.value });
  };

  render() {
    const { classes, columnDefinition, onRowClick, data, hover } = this.props;
    const { order, orderBy, rowsPerPage, page } = this.state;
    const emptyRows =
      rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);
    const WrappedTableHead = withStyles(tableHeadStyles)(EnhancedTableHead);

    return (
      <div className={classes.root}>
        <div className={classes.tableWrapper}>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <WrappedTableHead
              columnDefinition={columnDefinition}
              order={order}
              orderBy={orderBy}
              onRequestSort={this.handleRequestSort}
            />
            <TableBody>
              {stableSort(data, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(entry => {
                  const canceled =
                    entry.salesState === 'CANCELED' ||
                    entry.salesState === 'CANCELED_AND_TRANSFERRED' ||
                    entry.salesState === 'CANCELED_AND_REJECTED';

                  return (
                    <TableRow
                      key={entry.id}
                      hover={hover}
                      style={{ cursor: hover ? 'pointer' : 'inherit' }}
                      onClick={() => onRowClick(entry)}
                    >
                      <TableCell key={'id'} className={classes.tableCell}>
                        {entry.id}
                      </TableCell>
                      <TableCell
                        key={'type'}
                        className={
                          canceled
                            ? classes.canceledTableCell
                            : classes.tableCell
                        }
                      >
                        {entry.type}
                      </TableCell>
                      <TableCell key={'all'} className={classes.tableCell}>
                        <div className={classes.labelRow}>
                          <span className={classes.label}>Ticket Status</span>
                          <span
                            className={
                              classes[statusToColorMap[entry.salesState]]
                            }
                          >
                            {localization.de.ticketStatus[entry.salesState]}
                          </span>
                        </div>
                        <div className={classes.labelRow}>
                          <span className={classes.label}>Status DTA</span>
                          <span
                            className={
                              classes[statusToColorMap[entry.salesState]]
                            }
                          >
                            {entry.salesState}
                          </span>
                        </div>
                        {entry.refundWithoutPayment ? (
                          <div className={classes.labelRow}>
                            <span className={classes.label}>
                              Refund Without Payment
                            </span>
                            <span
                              className={classes[statusToColorMap.CANCELED]}
                            >
                              {'True'}
                            </span>
                          </div>
                        ) : null}
                        <div className={classes.labelRow}>
                          <span className={classes.label}>
                            Berechtigung DTA
                          </span>
                          <span
                            className={
                              classes[statusToColorMap[entry.permissionState]]
                            }
                          >
                            {
                              localization.de.permissionState[
                                entry.permissionState
                              ]
                            }
                          </span>
                        </div>

                        <div className={classes.labelRow}>
                          <span className={classes.label}>Person</span>
                          {entry.issuedTo}
                        </div>
                        <div className={classes.labelRow}>
                          <span className={classes.label}>Gültig ab</span>
                          {entry.validFrom}
                        </div>
                        <div className={classes.labelRow}>
                          <span className={classes.label}>Gruppe</span>
                          {entry.htagadiscount === true
                            ? 'Halbtax'
                            : entry.ageGroup}
                        </div>
                        {entry.keycard ? (
                          <div className={classes.labelRow}>
                            <span className={classes.label}>Keycard Nr.</span>
                            {entry.keycard}
                          </div>
                        ) : null}
                        <div className={classes.labelRow}>
                          <span className={classes.label}>
                            Berechtigungsnummer
                          </span>
                          {entry.permissionSerialNumber
                            ? entry.permissionSerialNumber
                            : '---'}
                        </div>
                        {entry.barcodeSerialNumber ? (
                          <div className={classes.labelRow}>
                            <span className={classes.label}>
                              Barcode Permission Serial Number
                            </span>
                            {entry.barcodeSerialNumber}
                          </div>
                        ) : null}
                        <div className={classes.labelRow}>
                          <span className={classes.label}>Reservationcode</span>
                          {entry.confirmationNumber}
                        </div>
                        {entry.addons ? (
                          <div className={classes.labelRow}>
                            <span className={classes.label}>Versicherung</span>
                            {entry.addons}
                          </div>
                        ) : null}
                        {entry.fees ? (
                          <div className={classes.labelRow}>
                            <span className={classes.label}>Gebühren</span>
                            {entry.fees}
                          </div>
                        ) : null}
                        <div className={classes.labelRow}>
                          <span className={classes.label}>Ticket Preis</span>
                          {entry.ticketPrice}
                        </div>
                        {entry.addons ? (
                          <div className={classes.labelRow}>
                            <span className={classes.label}>
                              Versicherungs Preis
                            </span>
                            {entry.addonPrice}
                          </div>
                        ) : null}
                        {entry.totalFees
                          ? console.log(entry.fees) || (
                              <div className={classes.labelRow}>
                                <span className={classes.label}>
                                  Total Gebühren
                                </span>
                                {entry.totalFees}
                              </div>
                            )
                          : null}
                        {entry.barcodeImage ? (
                          <div className={classes.labelRow}>
                            <span className={classes.label}>QrCode</span>
                            <span className={classes.labelRow}>
                              <ModalImage
                                buttonClass="modalButton"
                                title="QrCode"
                                content={entry.barcodeImage}
                              />
                            </span>
                          </div>
                        ) : null}
                        <div className={classes.labelRow}>
                          <span className={classes.label}>
                            Pricing Strategy
                          </span>
                          {entry.pricingStrategy}
                        </div>
                      </TableCell>
                      <TableCell
                        key={'price'}
                        className={
                          canceled ? classes.redTableCell : classes.tableCell
                        }
                      >
                        {entry.totalPrice}
                      </TableCell>
                      <TableCell key={'cancel'} className={classes.tableCell}>
                        {entry.action}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 49 * emptyRows }}>
                  <TableCell colSpan={6} className={classes.tableCell} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            'aria-label': 'Previous Page',
          }}
          nextIconButtonProps={{
            'aria-label': 'Next Page',
          }}
          onChangePage={this.handleChangePage}
          onChangeRowsPerPage={this.handleChangeRowsPerPage}
        />
      </div>
    );
  }
}

EnhancedTable.propTypes = {
  classes: PropTypes.object.isRequired,
  columnDefinition: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
    })
  ),
  hover: PropTypes.bool,
  data: PropTypes.array,
  onRowClick: PropTypes.func,
};

export default withStyles(styles)(EnhancedTable);



// WEBPACK FOOTER //
// ./src/components/Table/TicketOverviewTable.jsx